// src/pages/buyers/SupplierManagement.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  Rating,
  LinearProgress,
  Fade,
  Zoom,
  Badge,
  Stack,
  Skeleton,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  List as ListIcon,
  GridView as GridIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from './components/styles';
import { formatCurrency, formatDate } from './components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  whatsapp: '#25D366',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  SUPPLIERS: {
    LIST: `${API_BASE_URL}/suppliers/buyer-suppliers.php`,
  },
  PRODUCTS: {
    LIST: `${API_BASE_URL}/products/buyer-products.php`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[SUPPLIER-MGMT] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[SUPPLIER-MGMT] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[SUPPLIER-MGMT] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[SUPPLIER-MGMT] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const getInitials = (name) => {
  if (!name) return 'S';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// ============================================
// CUSTOM COMPONENTS
// ============================================
const SupplierCard = ({ supplier, onViewProducts }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Zoom in style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${COLORS.grey[200]}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#FFFFFF',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
            borderColor: COLORS.primary,
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha(COLORS.primary, 0.08),
                  color: COLORS.primary,
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                }}
              >
                {getInitials(supplier.company_name)}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                    {supplier.company_name}
                  </Typography>
                  {supplier.business_verified && (
                    <Tooltip title="Verified Supplier">
                      <VerifiedIcon sx={{ color: COLORS.success, fontSize: 20 }} />
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: COLORS.grey[400] }} />
                  <Typography variant="caption" color="textSecondary">
                    {supplier.office_address || 'Location not specified'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip
                    label={`${supplier.product_count || 0} Products`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.6rem',
                      bgcolor: alpha(COLORS.info, 0.08),
                      color: COLORS.info,
                      fontWeight: 600,
                    }}
                  />
                  {supplier.total_orders > 0 && (
                    <Chip
                      label={`${supplier.total_orders} Orders`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6rem',
                        bgcolor: alpha(COLORS.success, 0.08),
                        color: COLORS.success,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Rating 
                value={parseFloat(supplier.avg_rating || 0)} 
                size="small" 
                readOnly 
                precision={0.5} 
              />
              <Typography variant="caption" color="textSecondary">
                {supplier.total_reviews || 0} reviews
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Stats - Fixed overlapping issue */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Products
              </Typography>
              <Typography variant="h6" fontWeight={700} color={COLORS.primary} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {supplier.product_count || 0}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Avg Order
              </Typography>
              <Typography variant="body2" fontWeight={700} color={COLORS.accent} sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                {formatCurrency(supplier.avg_order_value || 0)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Stock
              </Typography>
              <Typography variant="h6" fontWeight={700} color={COLORS.success} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {supplier.total_stock || 0}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Price Range
              </Typography>
              <Typography variant="body2" fontWeight={600} color={COLORS.grey[700]} sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' } }}>
                {formatCurrency(supplier.min_price || 0)} - {formatCurrency(supplier.max_price || 0)}
              </Typography>
            </Grid>
          </Grid>

          {/* Categories */}
          {supplier.categories && supplier.categories.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {supplier.categories.slice(0, 4).map((cat, i) => (
                <Chip
                  key={i}
                  label={cat}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: alpha(COLORS.secondary, 0.08),
                    color: COLORS.secondary,
                    fontWeight: 500,
                  }}
                />
              ))}
              {supplier.categories.length > 4 && (
                <Chip
                  label={`+${supplier.categories.length - 4} more`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: alpha(COLORS.grey[400], 0.08),
                    color: COLORS.grey[600],
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          )}

          {/* Recent Products */}
          {supplier.recent_products && supplier.recent_products.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600}>
                Recent Products
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, overflowX: 'auto' }}>
                {supplier.recent_products.slice(0, 3).map((product) => (
                  <Box
                    key={product.id}
                    sx={{
                      minWidth: 80,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: COLORS.grey[50],
                      border: `1px solid ${COLORS.grey[200]}`,
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl(product.image) || `https://picsum.photos/seed/${product.id}/80/80`}
                      alt={product.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mx: 'auto',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${product.id}/80/80`;
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 500, fontSize: '0.6rem' }} noWrap>
                      {product.name}
                    </Typography>
                    <Typography variant="caption" color="primary" fontWeight={700} fontSize="0.6rem">
                      {formatCurrency(product.discountedPrice || product.price, product.currency)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
        <CardActions sx={{ p: 2.5, pt: 0, gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => onViewProducts(supplier)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              py: 1.2,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
              boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
              '&:hover': {
                boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            View Products
          </Button>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              bgcolor: COLORS.grey[100],
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            {expanded ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </CardActions>
      </Card>
    </Zoom>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const SupplierManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'buyer';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('/buyer/suppliers');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('all');
  const [sortBy, setSortBy] = useState('products');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid');

  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    totalProducts: 0,
    averageRating: 0,
  });

  // Dialog states
  const [supplierDetailDialog, setSupplierDetailDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchSuppliers = useCallback(async () => {
    logger.info('Fetching suppliers...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.SUPPLIERS.LIST}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        let suppliersData = [];
        
        if (result.data.suppliers) {
          suppliersData = result.data.suppliers;
        } else if (Array.isArray(result.data)) {
          suppliersData = result.data;
        }
        
        setSuppliers(suppliersData);
        calculateStats(suppliersData);
        logger.info(`✅ Loaded ${suppliersData.length} suppliers`);
      } else {
        throw new Error(result.message || 'Failed to fetch suppliers');
      }
    } catch (error) {
      logger.error('Error fetching suppliers:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load suppliers. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateStats = (suppliersData) => {
    const statsData = {
      total: suppliersData.length,
      verified: suppliersData.filter(s => s.business_verified).length,
      totalProducts: suppliersData.reduce((sum, s) => sum + (s.product_count || 0), 0),
      averageRating: suppliersData.reduce((sum, s) => sum + parseFloat(s.avg_rating || 0), 0) / (suppliersData.length || 1),
    };
    setStats(statsData);
  };

  const loadSupplierData = useCallback(async () => {
    await fetchSuppliers();
  }, [fetchSuppliers]);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || userType !== 'buyer') {
      navigate('/RawAuth');
      return;
    }
    loadSupplierData();
  }, [loadSupplierData, navigate, userType]);

  // ============================================
  // FILTERING & SORTING
  // ============================================
  useEffect(() => {
    let result = [...suppliers];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(supplier => 
        (supplier.company_name && supplier.company_name.toLowerCase().includes(term)) ||
        (supplier.office_address && supplier.office_address.toLowerCase().includes(term)) ||
        (supplier.categories && supplier.categories.some(cat => cat.toLowerCase().includes(term)))
      );
    }

    // Verification filter
    if (filterVerified === 'verified') {
      result = result.filter(s => s.business_verified);
    } else if (filterVerified === 'unverified') {
      result = result.filter(s => !s.business_verified);
    }

    // Sorting
    switch (sortBy) {
      case 'products':
        result.sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
        break;
      case 'rating':
        result.sort((a, b) => parseFloat(b.avg_rating || 0) - parseFloat(a.avg_rating || 0));
        break;
      case 'orders':
        result.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
        break;
      default:
        break;
    }

    setFilteredSuppliers(result);
  }, [suppliers, searchTerm, filterVerified, sortBy]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleTabChange = useCallback((tab) => {
  setSelectedTab(tab);
  navigate(tab);
}, [navigate]);

  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    loadSupplierData();
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'info' });
  }, [loadSupplierData]);

  const handleViewProducts = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setSupplierDetailDialog(true);
  }, []);

  const handleVisitChemMart = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderStats = () => (
    <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.primary, 0.04), border: `1px solid ${alpha(COLORS.primary, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.primary}>{stats.total}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Total Suppliers</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.success, 0.04), border: `1px solid ${alpha(COLORS.success, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.success}>{stats.verified}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Verified</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.info, 0.04), border: `1px solid ${alpha(COLORS.info, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.info}>{stats.totalProducts}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Total Products</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.warning, 0.04), border: `1px solid ${alpha(COLORS.warning, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.warning}>{stats.averageRating.toFixed(1)}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Avg Rating</Typography>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper sx={{ p: 2.5, mb: 3.5, borderRadius: 4, border: `1px solid ${COLORS.grey[200]}`, background: '#FFFFFF' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search suppliers by name, location..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filterVerified}
            onChange={(e) => setFilterVerified(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Suppliers</MenuItem>
            <MenuItem value="verified" sx={{ fontWeight: 500 }}>Verified</MenuItem>
            <MenuItem value="unverified" sx={{ fontWeight: 500 }}>Unverified</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="products" sx={{ fontWeight: 600 }}>Most Products</MenuItem>
            <MenuItem value="rating" sx={{ fontWeight: 500 }}>Highest Rated</MenuItem>
            <MenuItem value="orders" sx={{ fontWeight: 500 }}>Most Orders</MenuItem>
            <MenuItem value="name" sx={{ fontWeight: 500 }}>Name A-Z</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" sx={{ bgcolor: COLORS.grey[100], borderRadius: 2 }}>
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{
                bgcolor: viewMode === 'grid' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'grid' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <GridIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{
                bgcolor: viewMode === 'list' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'list' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const renderGridView = () => (
    <Grid container spacing={3}>
      {filteredSuppliers.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <BusinessIcon sx={{ fontSize: 64, color: COLORS.grey[300], mb: 2 }} />
            <Typography variant="h6" color="textSecondary" fontWeight={600}>No suppliers found</Typography>
            <Typography variant="body2" color="textSecondary">Try adjusting your search or filters</Typography>
          </Paper>
        </Grid>
      ) : (
        filteredSuppliers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((supplier) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={supplier.id}>
              <SupplierCard
                supplier={supplier}
                onViewProducts={handleViewProducts}
              />
            </Grid>
          ))
      )}
    </Grid>
  );

  const renderListView = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Supplier</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Products</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Rating</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Orders</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSuppliers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">No suppliers found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredSuppliers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((supplier) => (
                <TableRow key={supplier.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary, fontWeight: 700, fontSize: '0.8rem' }}>
                        {getInitials(supplier.company_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {supplier.company_name}
                          {supplier.business_verified && (
                            <VerifiedIcon sx={{ fontSize: 14, color: COLORS.success, ml: 0.5, verticalAlign: 'middle' }} />
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {supplier.office_address || 'No address'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={COLORS.primary}>
                      {supplier.product_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={parseFloat(supplier.avg_rating || 0)} size="small" readOnly precision={0.5} />
                      <Typography variant="caption" fontWeight={600}>
                        {parseFloat(supplier.avg_rating || 0).toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={COLORS.accent}>
                      {supplier.total_orders || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.business_verified ? 'Verified' : 'Unverified'}
                      size="small"
                      sx={{
                        bgcolor: supplier.business_verified ? alpha(COLORS.success, 0.1) : alpha(COLORS.warning, 0.1),
                        color: supplier.business_verified ? COLORS.success : COLORS.warning,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => handleViewProducts(supplier)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                      }}
                    >
                      View Products
                    </Button>
                  </TableCell>
                </TableRow>
              ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[6, 9, 12, 24]}
        component="div"
        count={filteredSuppliers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );

  const renderSupplierDetailDialog = () => (
    <Dialog
      open={supplierDetailDialog}
      onClose={() => setSupplierDetailDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      {selectedSupplier && (
        <>
          <DialogTitle sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(COLORS.primary, 0.08),
                    color: COLORS.primary,
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  {getInitials(selectedSupplier.company_name)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedSupplier.company_name}
                    {selectedSupplier.business_verified && (
                      <VerifiedIcon sx={{ fontSize: 20, color: COLORS.success, ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedSupplier.office_address || 'No address specified'}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setSupplierDetailDialog(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {/* Contact Info */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>Contact Information</Typography>
                  {selectedSupplier.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                      <Typography variant="body2">{selectedSupplier.email}</Typography>
                    </Box>
                  )}
                  {selectedSupplier.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                      <Typography variant="body2">{selectedSupplier.phone}</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>Business Details</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <InventoryIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">{selectedSupplier.product_count || 0} Products</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <ShoppingBagIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">{selectedSupplier.total_orders || 0} Orders</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">Avg Order: {formatCurrency(selectedSupplier.avg_order_value || 0)}</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Categories */}
            {selectedSupplier.categories && selectedSupplier.categories.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedSupplier.categories.map((cat, i) => (
                    <Chip key={i} label={cat} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Top Products */}
            {selectedSupplier.top_products && selectedSupplier.top_products.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Top Products
                </Typography>
                <Grid container spacing={2}>
                  {selectedSupplier.top_products.map((product) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={product.id}>
                      <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.grey[200]}` }}>
                        <Box
                          component="img"
                          src={getImageUrl(product.image) || `https://picsum.photos/seed/${product.id}/120/120`}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: 100,
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.src = `https://picsum.photos/seed/${product.id}/120/120`;
                          }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} noWrap>
                            {product.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight={700} color="primary">
                              {formatCurrency(product.price, product.currency)}
                            </Typography>
                            <Chip
                              label={`${product.order_count || 0} sold`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.5rem' }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setSupplierDetailDialog(false)}>Close</Button>
            <Button
              variant="contained"
              onClick={handleVisitChemMart}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              VISIT ChemMart
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && suppliers.length === 0) {
    return <Preloader text="Loading Suppliers..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
    <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={handleTabChange}  // <-- Add this line
  userData={userData}
  wishlistCount={0}
  cartItemCount={0}
  onLogout={handleLogout}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={() => {}}
       
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BusinessIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  Suppliers
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredSuppliers.length} suppliers found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Stats */}
          {renderStats()}

          {/* Filters */}
          {renderFilters()}

          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography variant="caption" color="textSecondary" fontWeight={600}>
              Showing {Math.min(filteredSuppliers.length, rowsPerPage)} of {filteredSuppliers.length} suppliers
            </Typography>
          </Box>

          {/* Suppliers List */}
          {viewMode === 'grid' ? renderGridView() : renderListView()}
        </Box>

        <Footer />
      </Box>

      {/* Dialogs */}
      {renderSupplierDetailDialog()}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 3, boxShadow: '0 16px 48px rgba(0,0,0,0.12)', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles */}
      <style>
        {`
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: ${COLORS.grey[100]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: ${COLORS.grey[300]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${COLORS.grey[400]};
          }
        `}
      </style>
    </Box>
  );
};

export default SupplierManagement;